import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone,
  faCircleCheck,
  faCircleInfo,
  faTriangleExclamation,
  faArrowUpRightFromSquare,
  faComment,
  faWindowMaximize,
} from '@fortawesome/free-solid-svg-icons';
import ChannelPreview from './ChannelPreview.js';
import './ChannelGuide.css';

const PURPLE = '#5616ea';

// Numbered step badge as inline SVG so the digit sits at the optical center
// of the circle regardless of value (same technique as TelegramConnectModal).
// CSS centering of digits in circles is unreliable because the glyph rides
// the alphabetic baseline; positioning the text baseline at cy + 0.35em puts
// the cap-height band on the circle's geometric center.
function StepNum({ n }) {
  return (
    <svg
      className="cguide-step-num"
      viewBox="0 0 28 28"
      width="28"
      height="28"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="14" cy="14" r="14" fill={PURPLE} />
      <text
        x="14"
        y="14"
        dy="0.35em"
        textAnchor="middle"
        fontSize="15"
        fontFamily="'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
        fontWeight="700"
        fill="#ffffff"
      >
        {n}
      </text>
    </svg>
  );
}

// Restricted inline markdown -> React nodes. Supports **bold**, `code`, and
// [text](https://url). Guide copy is authored by us (not user input), so this
// stays a tiny, safe tokenizer rather than dangerouslySetInnerHTML.
function renderRich(text) {
  if (!text) return null;
  const out = [];
  const re = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(`([^`]+)`)|(\*\*([^*]+)\*\*)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) {
      out.push(
        <a key={i} href={m[3]} target="_blank" rel="noopener noreferrer">
          {m[2]}
        </a>
      );
    } else if (m[4]) {
      out.push(<code key={i}>{m[5]}</code>);
    } else if (m[6]) {
      out.push(<strong key={i}>{m[7]}</strong>);
    }
    last = re.lastIndex;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// The same snippet the Snippet tab hands out, scoped to the selected agent.
// 'widget' is the site-wide floating bubble; 'iframe' is an inline embed.
function buildSnippet(botId, embed) {
  if (!botId) return '';
  if (embed === 'iframe') {
    return `<iframe
  src="https://chat.ultimo-bots.com/${botId}"
  style="width: 100%; height: 600px; border: none;"
  allow="microphone"
></iframe>`;
  }
  return `<div id="chat-widget-container" data-user-id="${botId}"></div>
<script src="https://robert-kloepsch.github.io/ultimo-bots-widget/dist/bundle.js" defer></script>
<a href="https://www.ultimo-bots.com" target="_blank" rel="noopener" style="display: none;">Our website uses an intelligent AI assistant powered by Ultimo Bots to improve customer service.</a>`;
}

function ChannelGuide({ guide, botId }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const snippet = buildSnippet(botId, guide.embed);

  const copy = () => {
    if (!snippet) return;
    navigator.clipboard
      .writeText(snippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const codeBlock = (
    <div className="cguide-code-wrap">
      <div className="cguide-code-head">
        <span className="cguide-code-lang">
          <FontAwesomeIcon icon={guide.embed === 'iframe' ? faWindowMaximize : faComment} />
          {guide.embed === 'iframe' ? t('cguide_code_lang_iframe') : t('cguide_code_lang_widget')}
        </span>
        <button type="button" className="cguide-copy" onClick={copy} disabled={!snippet}>
          <FontAwesomeIcon icon={copied ? faCircleCheck : faClone} />
          {copied ? t('cguide_copied') : t('cguide_copy_code')}
        </button>
      </div>
      {snippet ? (
        <pre className="cguide-code">
          <code>{snippet}</code>
        </pre>
      ) : (
        <div className="cguide-code-empty">
          {t('cguide_no_agent')}
        </div>
      )}
    </div>
  );

  const placedInline = guide.steps.some((s) => s.placeSnippet);

  return (
    <div className="cguide">
      <div className="cguide-intro">
        <FontAwesomeIcon className="cguide-intro-icon" icon={faCircleInfo} />
        <div className="cguide-intro-body">
          <p>{renderRich(t(`cguide_${guide.key}_intro`, guide.intro))}</p>
          <span className={`cguide-embed-chip ${guide.embed}`}>
            <FontAwesomeIcon icon={guide.embed === 'iframe' ? faWindowMaximize : faComment} />
            {guide.embed === 'iframe' ? t('cguide_chip_iframe') : t('cguide_chip_widget')}
          </span>
        </div>
      </div>

      {guide.planCaveat && (
        <div className="cguide-plan">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{renderRich(t(`cguide_${guide.key}_plancaveat`, guide.planCaveat))}</span>
        </div>
      )}

      {/* Order is intro → preview → steps: say what it is, show what it looks
          like, then how to install it. The plan caveat stays glued to the intro
          because it qualifies the same claim.
          Opt-in per guide (`preview: true`). Every platform guide carries an
          `embed` mode, so switching this to `guide.embed &&` would light the
          mockup up for all of them — kept to the two generic website channels
          until we want that. */}
      {guide.preview && guide.embed && (
        <ChannelPreview variant={guide.embed} botId={botId} />
      )}

      <ol className="cguide-steps">
        {guide.steps.map((step, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <li className="cguide-step" key={idx}>
            <StepNum n={idx + 1} />
            <div className="cguide-step-body">
              {step.title && <div className="cguide-step-title">{renderRich(t(`cguide_${guide.key}_step${idx + 1}_title`, step.title))}</div>}
              <div className="cguide-step-text">{renderRich(t(`cguide_${guide.key}_step${idx + 1}_body`, step.body))}</div>
              {step.placeSnippet && codeBlock}
            </div>
          </li>
        ))}
      </ol>

      {/* If research never flagged a paste step, fall back to showing the
          code block under the steps so the user always gets their snippet. */}
      {!placedInline && codeBlock}

      {guide.note && (
        <div className="cguide-note">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{renderRich(t(`cguide_${guide.key}_note`, guide.note))}</span>
        </div>
      )}

      {guide.docUrl && (
        <a className="cguide-doc" href={guide.docUrl} target="_blank" rel="noopener noreferrer">
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          {t('cguide_open_help', { name: guide.name })}
        </a>
      )}
    </div>
  );
}

export default ChannelGuide;
